<template>
    <h2 v-if="hasTailorYourCoverQuestions" class="text-[28px] leading-normal text-section-header font-extrabold">
        Tailor Your Cover
    </h2>
    <asli-form-section v-if="hasTailorYourCoverQuestions">
        <asli-radio
            v-if="hasAccidentalDamage"
            id="accidental_damage_cover"
            name="accidental_damage_cover"
            v-model="propertyDetails.has_buildings_contents_accidental_damage"
            label="Would you like to add full Accidental Damage cover?"
            :tooltipText="ADDITIONAL_DETAILS_HELP_TEXT.accidental_damage_cover"
            :options="lists.yes_no"
            :is-yes-no-option="true"
            rules="required:accidental_damage_cover"
            return-boolean
            @change="commitRisk"
        />
        <asli-radio
            v-if="hasUnspecifiedCover"
            id="unspecified_item_cover"
            name="unspecified_item_cover"
            v-model="coverDetails.unspecified_risk_amount"
            label="Would you like to add any cover for unspecified items?"
            :tooltipText="ADDITIONAL_DETAILS_HELP_TEXT.unspecified_item_cover"
            :options="unspecifiedItemsCoverOptions"
            rules="required:unspecified_item_cover"
            @change="commitRisk"
        />
        <asli-radio
            v-if="hasSpecifiedCover"
            id="specificed_item_cover"
            name="specificed_item_cover"
            v-model="coverDetails.has_specified_items"
            label="Would you like to add any cover for specified items?"
            :tooltipText="ADDITIONAL_DETAILS_HELP_TEXT.specificed_item_cover"
            :options="lists.yes_no"
            :is-yes-no-option="true"
            rules="required:specificed_item_cover"
            return-boolean
            @change="commitRisk"
        />
        <asli-repeater
            v-if="coverDetails.has_specified_items === true"
            id="specified_items_repeater"
            ref="repeaterRef"
            v-model="coverDetails.specified_items"
            :display-label="item => `${getItemValueName(item.type)}`"
            v-slot="{ item, index, updateLocal }"
            :add-label="`Add ${coverDetails.specified_items?.length ?? 0 > 0 ? 'Another' : 'an'} Item`"
            form-title="New Specified Item"
            :object-template="EMPTY_ITEM"
            background-color="bg-panel-tertiary"
            :description="ADDITIONAL_DETAILS_HELP_TEXT.specified_items_repeater"
            :show-additional-description="false"
            :always-show-cancel-btn="true"
            requiredRule="required"
        >
            <specified-item-form
                :index="index"
                :item="item"
                @update="
                    $event => {
                        updateLocal(index, $event);
                    }
                "
            />
        </asli-repeater>
        <asli-radio
            v-if="hasSpecifiedCover"
            id="sports_equipment_cover"
            name="sports_equipment_cover"
            v-model="coverDetails.has_sports_equipment"
            label="Would you like to add any cover for sports equipment?"
            :tooltipText="ADDITIONAL_DETAILS_HELP_TEXT.sports_equipment_cover"
            :options="lists.yes_no"
            :is-yes-no-option="true"
            rules="required"
            return-boolean
            @change="commitRisk"
        />
        <asli-text-input
            v-if="hasSpecifiedCover && coverDetails.has_sports_equipment"
            id="sports_equipment_amount"
            name="sports_equipment_amount"
            rules="required:sports_equipment_amount|minAmount:300,item_amount|maxAmount:99999,item_amount"
            v-model="coverDetails.sports_equipment_amount"
            label="How much sports equipment cover is required?"
            type="text"
            large
            :icon="euroIcon()"
            :mask="COMMA_NUMBER_MASK(10)"
        ></asli-text-input>
    </asli-form-section>
</template>

<script lang="ts" setup>
import { AsliFormSection, AsliRadio, AsliRepeater, AsliTextInput } from "@mbs-npm/anpost-ui";
import { useListStore } from "@/stores/lists/lists";
import { computed, onMounted, reactive, ref, watch, toRaw } from "vue";
import { useProposalStore } from "@/stores/risk/risk";
import { unspecifiedItemsCoverOptions, euroIcon } from "@/static/constants";
import { CoverDetails, PropertyDetails } from "@/stores/risk/types/types";
import { EMPTY_ITEM } from "@/stores/risk/types/repeater/empty-objects";
import { ADDITIONAL_DETAILS_HELP_TEXT } from "@/utils/helpText";
import SpecifiedItemForm from "@/components/RepeaterForms/SpecifiedItemForm.vue";
import { useDetectFormChanges } from "@/hooks/useDetectFormChanges";
import { AsliRepeaterInstance } from "@/stores/risk/types/repeater/repeater-types";
import { COMMA_NUMBER_MASK } from "@/static/masks";

const proposalStore = useProposalStore();
const proposal = computed(() => proposalStore?.proposal);
const storedRisk = proposalStore.proposal.risk;

const listStore = useListStore();
const lists = computed(() => listStore?.lists);

const emit = defineEmits<{ (e: "tailorFieldChanged", val: boolean): void }>();

const quote = computed(() => proposal.value?.quote);
const postQuoteQuestions = computed(() => quote.value?.post_quote_questions);
const repeaterRef = ref<AsliRepeaterInstance | null>(null);

const hasAccidentalDamage = computed(() => postQuoteQuestions.value?.has_accidental_damage);
const hasSpecifiedCover = computed(() => postQuoteQuestions.value?.has_specified_cover);
const hasUnspecifiedCover = computed(() => postQuoteQuestions.value?.has_unspecified_cover);

const hasTailorYourCoverQuestions = computed(() => {
    return hasAccidentalDamage.value || hasUnspecifiedCover.value || hasSpecifiedCover.value;
});

const coverDetails = reactive<Partial<CoverDetails>>({
    has_specified_items: storedRisk.cover_details?.has_specified_items ?? false,
    specified_items: storedRisk.cover_details?.specified_items ?? [],
    unspecified_risk_amount: storedRisk.cover_details?.unspecified_risk_amount ?? 0,
    has_sports_equipment: storedRisk.cover_details?.has_sports_equipment ?? false,
    sports_equipment_amount: storedRisk.cover_details?.sports_equipment_amount ?? null
});

const propertyDetails = reactive<Partial<PropertyDetails>>({
    has_buildings_contents_accidental_damage:
        storedRisk.property_details?.has_buildings_contents_accidental_damage ?? false
});

async function validateRepeater() {
    if (repeaterRef && repeaterRef.value) {
        try {
            if (repeaterRef.value?.itemToEdit) {
                const hasUnsavedForm = repeaterRef.value.itemToEdit.value !== null;

                if (hasUnsavedForm) {
                    const currentIndex = repeaterRef.value?.itemToEdit?.value;

                    if (currentIndex !== null && currentIndex !== undefined) {
                        const currentItem = coverDetails.specified_items
                            ? coverDetails.specified_items[currentIndex]
                            : null;

                        try {
                            const saveResult = await repeaterRef.value?.saveForm(currentIndex, currentItem);

                            if (!saveResult) {
                                console.log("Failed to save repeater form");
                                return;
                            }
                        } catch (error) {
                            console.error("Error saving repeater form:", error);
                            return;
                        }
                    }
                }
            }

            const isRepeaterValid = await repeaterRef.value?.validateCurrentForm(true);

            if (isRepeaterValid === false) {
                return false;
            }
            return true;
        } catch (error) {
            return false;
        }
    }
}

function getItemValueName(item) {
    if (!lists.value.specified_item_type) {
        return item;
    }
    return lists.value.specified_item_type.find(option => option.value === item)?.name || item;
}

const { checkFormHasChanges, updateFormValues, resetInitialValues, initialiseValues } = useDetectFormChanges();

function buildFormValues() {
    return {
        has_buildings_contents_accidental_damage: propertyDetails.has_buildings_contents_accidental_damage,
        has_specified_items: coverDetails.unspecified_risk_amount,
        has_unspecified_risk_amount: coverDetails.has_specified_items,
        has_sports_equipment: coverDetails.has_sports_equipment,
        sports_equipment_amount: coverDetails.sports_equipment_amount
    };
}

function doReset() {
    resetInitialValues();
}

defineExpose({
    doReset,
    validateRepeater,
    validateCurrentForm: repeaterRef?.value?.validateCurrentForm ?? true
});

onMounted(() => {
    initialiseValues(buildFormValues());
    if (!hasUnspecifiedCover.value) {
        coverDetails.unspecified_risk_amount = null;
        coverDetails.has_specified_items = null;
        coverDetails.specified_items = [];
    }
});

function commitRisk() {
    updateFormValues(buildFormValues());
    if (coverDetails.specified_items) {
        coverDetails.specified_items.map(item => {
            item.value = item.value && parseInt(item.value.toString().replace(/,/g, ""));
            return item;
        });
    }
    if (!coverDetails.has_sports_equipment) {
        coverDetails.sports_equipment_amount = null;
    }
    proposalStore.patchCoverDetails(coverDetails);
    proposalStore.patchPropertyDetails(propertyDetails);
    emit("tailorFieldChanged", checkFormHasChanges());
}

function resetSpecifiedItemsForPropertyChange() {
    coverDetails.has_specified_items = null;
    coverDetails.specified_items = [];
    proposalStore.patchCoverDetails({
        ...storedRisk.cover_details,
        has_specified_items: null,
        specified_items: [],
        unspecified_risk_amount: 0
    });
}

watch(
    () => quote.value?.post_quote_questions,
    (newQuestions, oldQuestions) => {
        if (
            newQuestions?.has_specified_cover !== oldQuestions?.has_specified_cover &&
            !newQuestions?.has_specified_cover
        ) {
            resetSpecifiedItemsForPropertyChange();
        }
    },
    { deep: true }
);
const areObjectsEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(key => {
        const val1 = obj1[key];
        const val2 = obj2[key];

        if (typeof val1 === "object" && val1 !== null && typeof val2 === "object" && val2 !== null) {
            return areObjectsEqual(val1, val2);
        }

        return val1 === val2;
    });
};

const areArraysEqual = (arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((val, index) => {
        const otherVal = arr2[index];

        if (typeof val === "object" && val !== null && typeof otherVal === "object" && otherVal !== null) {
            return areObjectsEqual(val, otherVal);
        }

        return val === otherVal;
    });
};

watch(
    () => coverDetails.specified_items,
    (newValue, oldValue) => {
        if (oldValue) {
            for (let i = 0; i < oldValue?.length; i++) {
                oldValue[i] = toRaw(oldValue[i]);
            }
        }
        if (newValue) {
            for (let i = 0; i < newValue?.length; i++) {
                newValue[i] = toRaw(newValue[i]);
            }
        }
        if (newValue?.length === 0) {
            coverDetails.has_specified_items = false;
        }
        if (!areArraysEqual(toRaw(newValue), toRaw(oldValue))) {
            commitRisk();
            emit("tailorFieldChanged", true);
        }
    },
    { deep: true }
);

watch(
    () => coverDetails.sports_equipment_amount,
    (newValue, oldValue) => {
        if (newValue && newValue !== oldValue && coverDetails.has_sports_equipment) {
            commitRisk();
        }
    },
    { deep: true }
);
</script>
